<template>
    <div class="venue-billing-history-table">
        <div id="preloader" v-if="load" class="preloader"></div>
        <base-data-table
            :columns="columns"
            :originalData="data"
            :maxRow="20"
            :styles="tableStyles"
        >
            <template #mobile-view="{ mobileData }">
                <div class="mobile-view">
                    <div class="billing-histories">
                        <div
                            v-for="(history, indx) in mobileData"
                            :key="indx"
                            class="billing-history"
                        >
                            <div class="header">
                                <div class="billing-info">
                                    <p class="billing-id">
                                        {{ history.invoice_number.value }}
                                    </p>
                                    <p class="billing-type">{{ history.type.value }}</p>
                                </div>
                                <p class="billing-date">{{ history.invoice_date.value }}</p>
                            </div>
                            <div class="payment-info">
                                <div class="info">
                                    <span class="type-icon">
                                        <img
                                            :src="history.payment_info.value.icon"
                                            alt="type-icon"
                                        />
                                    </span>
                                    <div class="card-last4">
                                        {{ history.payment_info.value.last4 }}
                                    </div>
                                </div>
                                <p
                                    :style="
                                        amountColor(
                                            history.type.value,
                                            history.status.value.status
                                        )
                                    "
                                    class="amount"
                                >
                                    {{ history.total_amount.value.val }}
                                </p>
                            </div>
                            <div class="event-info">
                                <div class="name">{{ history.details.value.name }}</div>
                                <div v-if="history.details.value.event_published" class="info">
                                    <a
                                        :href="`${URLS.redirect.event}/${history.details.value.id}`"
                                        target="_blank"
                                        class="date"
                                    >
                                        {{ history.details.value.date }}
                                    </a>
                                    <a
                                        :href="`${URLS.redirect.event}/${history.details.value.id}`"
                                        target="_blank"
                                        class="title"
                                    >
                                        {{ history.details.value.title }}
                                    </a>
                                </div>
                                <div v-else class="info">
                                    <p class="date">
                                        {{ history.details.value.date }}
                                    </p>
                                    <p class="title">
                                        {{ history.details.value.title }}
                                    </p>
                                </div>
                            </div>
                            <div class="status-info">
                                <p
                                    class="transaction-status-pill mb-0"
                                    :style="pillColor(history.status.value.status)"
                                >
                                    {{ history.status.value.status }}
                                </p>
                                <p
                                    v-if="showStatusDetail(history.status.value)['show']"
                                    @click="
                                        showStatusDetail(history.status.value)['func']
                                            ? showStatusDetail(history.status.value)['call']()
                                            : () => {}
                                    "
                                    class="transaction-clear-date"
                                    :class="showStatusDetail(history.status.value)['class']"
                                >
                                    {{ showStatusDetail(history.status.value)['value'] }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #table-header="{ header, headers, sortBy }">
                <div class="billing-table-header">
                    <p :class="header.classes.join(' ')">
                        {{ header.value }}
                    </p>
                    <button
                        v-if="header.actions.sortDirection"
                        @click="sort(header, headers, sortBy)"
                        class="sort-btn"
                    >
                        <img
                            v-if="
                                header.actions.sortDirection === 'off' ||
                                header.actions.sortDirection === 'desc'
                            "
                            :src="URLS.icons.sortUpEmpty"
                            alt="sort-up-empty"
                        />
                        <img v-else :src="URLS.icons.sortUpFilled" alt="sort-up-filled" />
                        <img
                            v-if="
                                header.actions.sortDirection === 'off' ||
                                header.actions.sortDirection === 'asc'
                            "
                            :src="URLS.icons.sortDownEmpty"
                            alt="sort-down-empty"
                        />
                        <img v-else :src="URLS.icons.sortDownFilled" alt="sort-up-filled" />
                    </button>
                </div>
            </template>
            <template #table-cell="{ cell }">
                <div v-if="cell.accessor === 'payment_info'" :class="cell.classes.join(' ')">
                    <img
                        :style="cell.value.type ? {} : { width: '27px' }"
                        :src="cell.value.icon"
                        class="payment-type-icon"
                        alt="payment-type"
                    />
                    <p class="last4">{{ cell.value.last4 }}</p>
                </div>
                <div v-else-if="cell.accessor === 'status'" :class="cell.classes.join(' ')">
                    <p class="transaction-status-pill" :style="pillColor(cell.value.status)">
                        {{ cell.value.status }}
                    </p>
                    <p
                        v-if="showStatusDetail(cell.value)['show']"
                        @click="
                            showStatusDetail(cell.value)['func']
                                ? showStatusDetail(cell.value)['call']()
                                : () => {}
                        "
                        class="transaction-clear-date"
                        :class="showStatusDetail(cell.value)['class']"
                    >
                        {{ showStatusDetail(cell.value)['value'] }}
                    </p>
                </div>
                <div v-else-if="cell.accessor === 'details'" :class="cell.classes.join(' ')">
                    <p>
                        {{ cell.value.name }}
                    </p>
                    <a
                        v-if="cell.value.event_published"
                        :href="`${URLS.redirect.event}/${cell.value.id}`"
                        target="_blank"
                        class="transaction-detail-event"
                    >
                        {{ `${cell.value.date}` }}
                    </a>
                    <p v-else class="transaction-detail-event">
                        {{ `${cell.value.date}` }}
                    </p>
                </div>
                <p
                    v-else-if="cell.accessor === 'total_amount'"
                    :style="amountColor(cell.value.type, cell.value.status)"
                    :class="cell.classes.join(' ')"
                >
                    {{ cell.value.val }}
                </p>
                <p v-else :class="cell.classes.join(' ')">
                    {{ cell.value }}
                </p>
            </template>
            <template
                #paginator="{ totalPages, currentPage, nextPage, prevPage, paginatedPages }"
            >
                <div class="billing-history-pagination">
                    <button @click="prevPage" class="prev-page">
                        <img :src="URLS.icons.leftArrow" alt="prev-page" />
                    </button>
                    <span
                        v-for="(page, indx) in paginatedPages"
                        :key="indx"
                        :style="page.highlight ? { color: '#F5F5F5' } : {}"
                        class="paginator-pages"
                    >
                        {{ page.page }}
                    </span>
                    <button @click="nextPage" class="next-page">
                        <img :src="URLS.icons.rightArrow" alt="right-page" />
                    </button>
                </div>
            </template>
        </base-data-table>
    </div>
</template>
<script>
    // responsible for grabbing data, setting up column def
    // and passing into base table

    import BaseDataTable from './BaseDataTable'
    import { useBillingHistory } from '@/composables/features/billing-history/useBillingHistory'
    import useCheckScreen from '@/composables/useCheckScreen'
    export default {
        name: 'VenueBillingHistoryTable',
        components: {
            BaseDataTable
        },
        props: {
            role: {
                required: true,
                type: String
            },
            user: {
                required: true,
                type: Number
            }
        },
        setup(props) {
            const { isMobile } = useCheckScreen()
            const {
                URLS,
                load,
                columns,
                data,
                pillColor,
                amountColor,
                showStatusDetail,

                sort
            } = useBillingHistory(props)

            return {
                URLS,
                load,
                columns,
                data,
                pillColor,
                amountColor,
                showStatusDetail,
                sort,

                isMobile
            }
        },
        data: () => ({
            tableStyles: {
                table: {
                    'max-width': '1048px',
                    width: '100%',
                    position: 'relative',
                    'margin-bottom': '114px'
                },
                header: {
                    'border-top': '1px solid #262626',
                    'border-bottom': '1px solid #262626'
                },
                headerSpacing: {
                    padding: '8px 4px'
                },
                row: {
                    'border-bottom': '1px solid #262626'
                },
                rowSpacing: {
                    padding: '16px 4px'
                }
            }
        })
    }
</script>
<style lang="scss" scoped>
    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    .venue-billing-history-table {
        width: 100%;
    }
    .invoice-cell {
        color: #8b8b8b;
        font-family: 'Instrument Sans';
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: 133%;
    }
    .invoice-cell-id {
        min-width: 112px;
        text-wrap: nowrap;
    }
    .invoice-cell-date {
        min-width: 110px;
    }
    .invoice-cell-type {
        min-width: 120px;
    }
    .invoice-cell-details {
        min-width: 418px;
    }
    .invoice-cell-payment {
        min-width: 50px;
    }
    .invoice-cell-amount {
        min-width: 104px;
    }
    .invoice-cell-status {
        min-width: 80px;
    }
    .stack {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }
    .stack-center {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    .white-text {
        color: #dedede;
    }
    .payment-type-icon {
        width: 36px;
        height: 24px;
        object-fit: cover;
    }
    .last4 {
        color: #8b8b8b;
        font-family: 'Instrument Sans';
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 135%;
        letter-spacing: 0.12px;
    }
    .transaction-status-pill {
        margin-bottom: 4px;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 2px 8px;
        border-radius: 120px;
        color: #000;
        font-family: 'Instrument Sans';
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 135%;
        letter-spacing: 0.12px;
        text-wrap: nowrap;
    }
    .transaction-detail-event {
        overflow: hidden;
        color: #acacac;
        text-overflow: ellipsis;
        font-family: 'Instrument Sans';
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 135%;
        letter-spacing: 0.12px;
        text-decoration: none;
    }
    .provide-details {
        color: #8b8b8b;
        font-family: 'Instrument Sans';
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 135%;
        letter-spacing: 0.12px;
        cursor: pointer;
        text-decoration-line: underline;
    }
    .clear-date {
        color: #8b8b8b;
        font-family: 'Instrument Sans';
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 135%;
        letter-spacing: 0.12px;
    }
    .billing-history-pagination {
        position: absolute;
        bottom: -114px;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        margin-top: 66px;

        .paginator-pages {
            width: 48px;
            height: 48px;
            color: #8b8b8b;
            text-align: center;
            font-family: 'Instrument Sans';
            font-size: 18px;
            font-style: normal;
            font-weight: 500;
            line-height: 133%; /* 23.94px */
            padding: 12px 0px;
        }
        .prev-page,
        .next-page {
            background: none;
            width: 48px;
            height: 48px;
            padding: 12px;

            img {
                width: 24px;
                height: 24px;
                object-fit: cover;
            }
        }
    }
    .billing-table-header {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 4px;

        .billing-header {
            color: #f5f5f5;
            font-family: 'Instrument Sans';
            font-size: 12px;
            font-style: normal;
            font-weight: 500;
            line-height: 135%;
            letter-spacing: 0.12px;
        }
        .sort-btn {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: -4px;
            padding: 6px 0px;
            background: none;
        }
    }
    .mobile-view {
        padding: 0px;

        .billing-histories:first-child {
            border-top: 1px solid #262626;
        }
        .billing-histories {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 0px;

            .billing-history {
                padding: 8px 8px 24px 8px;
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: center;
                gap: 16px;
                border-bottom: 1px solid #262626;
                .header {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;

                    .billing-info {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                        align-items: center;
                        gap: 8px;

                        .billing-id,
                        .billing-type {
                            color: #8b8b8b;
                            font-family: 'Instrument Sans';
                            font-size: 16px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 125%;
                        }
                    }
                    .billing-date {
                        color: #8b8b8b;
                        font-family: 'Instrument Sans';
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 125%;
                    }
                }
                .payment-info {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;

                    .info {
                        display: flex;
                        align-items: center;

                        .type-icon {
                            display: flex;
                            flex-direction: row;
                            align-items: center;
                            justify-content: center;
                            height: 36px;
                            width: 36px;
                            padding: 3.857px 9.857px;

                            img {
                                width: 36px;
                                height: 24px;
                                object-fit: cover;
                            }
                        }
                        .card-last4 {
                            color: #8b8b8b;
                            font-family: 'Instrument Sans';
                            font-size: 12px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 135%;
                            letter-spacing: 0.12px;
                        }
                    }
                    .amount {
                        color: #cdeb09;
                        text-align: right;
                        font-family: 'Instrument Sans';
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 133%;
                    }
                }
                .event-info {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;

                    .name {
                        overflow: hidden;
                        color: #dedede;
                        text-overflow: ellipsis;
                        font-family: 'Instrument Sans';
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 125%;
                    }
                    .info {
                        display: flex;
                        flex-direction: row;
                        justify-content: flex-start;
                        align-items: center;
                        gap: 8px;

                        .date,
                        .title {
                            color: #acacac;
                            font-family: 'Instrument Sans';
                            font-size: 12px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 135%;
                            letter-spacing: 0.12px;
                            text-decoration: none;
                        }
                    }
                }
                .status-info {
                    width: 100%;
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 8px;
                }
            }
        }
    }
</style>
